import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import { canonicalMealsCsv } from './mealTypesDropdown';

function authHeaders() {
    const token = localStorage.getItem('Token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function pickValue(obj, ...keys) {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const key of keys) {
        const val = obj[key];
        if (val !== undefined && val !== null && val !== '') return val;
    }
    return undefined;
}

export function normalizeCateringTempPackageItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const lineId = pickValue(
        raw,
        'cateringTempItemId',
        'Catering_temp_Item_Id',
        'Catering_Temp_Item_Id',
        'id'
    );
    const cateringItemId = pickValue(raw, 'cateringItemId', 'Catering_Item_Id');
    if (lineId == null && cateringItemId == null) return null;
    return {
        localKey: pickValue(raw, 'localKey', '_localKey', 'local_key') ?? null,
        cateringTempItemId: lineId != null ? Number(lineId) : null,
        cateringTempId: pickValue(raw, 'cateringTempId', 'Catering_Temp_Id'),
        cateringItemId: cateringItemId != null ? Number(cateringItemId) : null,
        itemName: pickValue(raw, 'itemName', 'Item_Name', 'foodItemName') ?? '',
        price: pickValue(raw, 'price', 'Price') ?? '',
        foodCategoryId: pickValue(raw, 'foodCategoryId', 'food_category_id', 'Food_Category_Id'),
        foodCategory:
            pickValue(raw, 'foodCategory', 'foodCategoryName', 'Food_Category', 'food_category') ??
            '',
        foodType: pickValue(raw, 'foodType', 'Food_Type', 'itemFoodType') ?? '',
        meal: canonicalMealsCsv(pickValue(raw, 'meal', 'Meal') ?? ''),
        isAvailable: pickValue(raw, 'isAvailable', 'Is_Available'),
        createdBy: pickValue(raw, 'createdBy', 'Created_By', 'createdById', 'Created_By_Id'),
        createdDate: pickValue(raw, 'createdDate', 'created_date', 'Created_Date'),
        updatedBy: pickValue(raw, 'updatedBy', 'Updated_By', 'updatedById', 'Updated_By_Id'),
        updatedDate: pickValue(raw, 'updatedDate', 'updated_date', 'Updated_Date'),
    };
}

export function normalizeCateringTemp(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = pickValue(raw, 'cateringTempId', 'Catering_Temp_Id', 'id');
    if (id == null) return null;
    const statusRaw = pickValue(raw, 'status', 'Status');
    const status =
        statusRaw === true ||
        statusRaw === 1 ||
        statusRaw === '1' ||
        String(statusRaw ?? '').trim().toLowerCase() === 'true' ||
        String(statusRaw ?? '').trim().toUpperCase() === 'ACTIVE';
    const base = {
        cateringTempId: Number(id),
        restaurantId: pickValue(raw, 'restaurantId', 'Restaurant_Id'),
        packageName: pickValue(raw, 'packageName', 'Package_Name', 'package_name') ?? '',
        description: pickValue(raw, 'description', 'Description') ?? '',
        pricePerPerson: pickValue(raw, 'pricePerPerson', 'Price_Per_Person', 'price_per_person'),
        status,
        createdBy: pickValue(raw, 'createdBy', 'Created_By'),
        createdDate: pickValue(raw, 'createdDate', 'created_date', 'Created_Date'),
        updatedBy: pickValue(raw, 'updatedBy', 'Updated_By'),
        updatedDate: pickValue(raw, 'updatedDate', 'updated_date', 'Updated_Date'),
    };
    if (Array.isArray(raw.items)) {
        base.items = raw.items.map(normalizeCateringTempPackageItem).filter(Boolean);
    }
    return base;
}

export function packageItemToCreatePayload(item, actorId) {
    const row = normalizeCateringTempPackageItem(item) || item;
    const payload = {
        cateringItemId: Number(row.cateringItemId),
        meal: canonicalMealsCsv(row.meal ?? ''),
    };
    if (actorId != null && actorId !== '') {
        payload.createdById = Number(actorId);
    }
    return payload;
}

export function packageItemToUpdatePayload(item, actorId) {
    const row = normalizeCateringTempPackageItem(item) || item;
    const payload = {
        meal: canonicalMealsCsv(row.meal ?? ''),
    };
    if (actorId != null && actorId !== '') {
        payload.updatedById = Number(actorId);
    }
    return payload;
}

export function getPackageItemRowKey(item) {
    const row = normalizeCateringTempPackageItem(item) || item;
    if (row.cateringTempItemId != null) return row.cateringTempItemId;
    if (row.localKey) return row.localKey;
    return String(row.cateringItemId ?? '');
}

export function packageCateringItemIdsInUse(items) {
    return new Set(
        (items || [])
            .map((item) => {
                const row = normalizeCateringTempPackageItem(item) || item;
                const id = row?.cateringItemId;
                return id != null && id !== '' ? String(id) : null;
            })
            .filter(Boolean)
    );
}

export async function fetchCateringTemps(restaurantId) {
    if (!restaurantId) return [];
    const response = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.cateringTempByRestaurantId(restaurantId),
        { headers: authHeaders() }
    );
    const data = response.data || {};
    const items = Array.isArray(data.items) ? data.items : [];
    return items.map(normalizeCateringTemp).filter(Boolean);
}

export async function fetchCateringTempById(tempId) {
    const response = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.cateringTempItemById(tempId),
        { headers: authHeaders() }
    );
    return normalizeCateringTemp(response.data);
}

export async function createCateringTemp(payload) {
    return axiosRestaurantInstance.post(RESTAURANT_API_PATHS.cateringTemp, payload, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
}

export async function updateCateringTemp(tempId, payload) {
    return axiosRestaurantInstance.put(RESTAURANT_API_PATHS.cateringTempItemById(tempId), payload, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
}

export async function deleteCateringTemp(tempId) {
    return axiosRestaurantInstance.delete(RESTAURANT_API_PATHS.cateringTempItemsDelete(tempId), {
        headers: authHeaders(),
    });
}

export async function addCateringTempPackageItems(cateringTempId, items, { createdBy } = {}) {
    const body = { cateringTempId, items };
    if (createdBy != null && createdBy !== '') {
        body.createdBy = Number(createdBy);
    }
    return axiosRestaurantInstance.post(
        RESTAURANT_API_PATHS.cateringTempPackageItems,
        body,
        { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );
}

export async function updateCateringTempPackageItem(lineId, payload) {
    return axiosRestaurantInstance.put(
        RESTAURANT_API_PATHS.cateringTempPackageItemById(lineId),
        payload,
        { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );
}

export async function deleteCateringTempPackageItem(lineId) {
    return axiosRestaurantInstance.delete(
        RESTAURANT_API_PATHS.cateringTempPackageItemById(lineId),
        { headers: authHeaders() }
    );
}

/**
 * Apply package line-item changes on Update: delete removed rows, update meals, add new rows.
 */
export async function syncCateringTempPackageItems(cateringTempId, initialItems, nextItems, actorId) {
    const initial = (initialItems || []).map(normalizeCateringTempPackageItem).filter(Boolean);
    const next = (nextItems || []).map(normalizeCateringTempPackageItem).filter(Boolean);

    const nextLineIds = new Set(
        next
            .filter((item) => item.cateringTempItemId != null)
            .map((item) => Number(item.cateringTempItemId))
    );

    for (const item of initial) {
        if (item.cateringTempItemId == null) continue;
        if (!nextLineIds.has(Number(item.cateringTempItemId))) {
            await deleteCateringTempPackageItem(item.cateringTempItemId);
        }
    }

    const initialByLineId = new Map(
        initial
            .filter((item) => item.cateringTempItemId != null)
            .map((item) => [Number(item.cateringTempItemId), item])
    );

    for (const item of next) {
        if (item.cateringTempItemId == null) continue;
        const prev = initialByLineId.get(Number(item.cateringTempItemId));
        if (!prev) continue;
        if (canonicalMealsCsv(prev.meal ?? '') !== canonicalMealsCsv(item.meal ?? '')) {
            await updateCateringTempPackageItem(
                item.cateringTempItemId,
                packageItemToUpdatePayload(item, actorId)
            );
        }
    }

    const toAdd = next.filter((item) => item.cateringTempItemId == null);
    if (toAdd.length > 0) {
        await addCateringTempPackageItems(
            cateringTempId,
            toAdd.map((item) => packageItemToCreatePayload(item, actorId)),
            { createdBy: actorId }
        );
    }
}
